import { useDashboard, useHoursStatus } from '../api/projects.js';
import { Alert, Card, Spinner } from './ui.js';
import { BuildStatusBadge, ProjectStatusBadge, StoryStatusBadge } from '../status.js';

/** Formats equivalent developer hours compactly, e.g. `6h` or `6.5h`. */
function fmtHours(hours: number): string {
  return `${Number.isInteger(hours) ? hours : Number(hours.toFixed(1))}h`;
}

/**
 * The dashboard: the "where your project is" summary (status + build progress),
 * then recent work, what's in progress, and what's remaining.
 */
export function ProjectDashboardDetails({ projectId }: { projectId: string }) {
  const dash = useDashboard(projectId);

  if (dash.isPending) return <Card>{<Spinner />}</Card>;
  if (dash.isError || !dash.data) {
    return <Card>{<Alert>Could not load the dashboard.</Alert>}</Card>;
  }
  const d = dash.data;
  const pct = d.counts.total > 0 ? Math.round((d.counts.done / d.counts.total) * 100) : 0;

  return (
    <div className="flex flex-col gap-6">
      <HoursBudget projectId={projectId} />

      <Card>
        <div className="mb-3 flex items-center gap-3">
          <h2 className="font-title text-xl tracking-wide">Where your project is</h2>
          <ProjectStatusBadge status={d.status} />
        </div>
        {d.counts.total === 0 ? (
          <p className="text-sm opacity-70">
            Progress appears here once the specification is split into user stories.
          </p>
        ) : (
          <>
            <div className="mb-2 flex items-center justify-between text-sm">
              <span className="font-medium">
                {d.counts.done} of {d.counts.total} stories built
              </span>
              <span className="opacity-60">{pct}%</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-neutral">
              <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
            </div>
            <div className="mt-4 flex flex-wrap gap-6">
              <Stat label="Built" value={d.counts.done} />
              <Stat label="In progress" value={d.counts.building} />
              <Stat label="Remaining" value={d.counts.remaining} />
            </div>
          </>
        )}
      </Card>

      <div className="grid gap-4 sm:grid-cols-2">
        <RecentCard title="Done in the last week" titles={d.doneLastWeek} window="7 days" />
        <RecentCard title="Done in the last month" titles={d.doneLastMonth} window="30 days" />
      </div>

      {d.building.length > 0 ? (
        <Card>
          <h3 className="mb-2 font-title text-xl tracking-wide">In progress now</h3>
          <ul className="flex flex-col gap-1 text-sm">
            {d.building.map((s, i) => {
              const failed = s.buildStatus === 'failed' || s.buildStatus === 'interrupted';
              return (
                <li key={i} className="flex items-center gap-2">
                  <span
                    className={`h-2 w-2 flex-shrink-0 rounded-full ${
                      failed ? 'bg-red-500' : 'animate-pulse bg-primary'
                    }`}
                  />
                  <span className="flex-1">
                    {s.title}
                    {s.epic ? <span className="opacity-50"> · {s.epic}</span> : null}
                  </span>
                  {s.buildStatus ? <BuildStatusBadge status={s.buildStatus} /> : null}
                </li>
              );
            })}
          </ul>
        </Card>
      ) : null}

      <Card>
        <h3 className="mb-2 font-title text-xl tracking-wide">What’s remaining</h3>
        {d.remaining.length === 0 ? (
          <p className="opacity-70">
            {d.counts.total === 0
              ? 'No stories yet.'
              : 'Nothing left — every story has been built. 🎉'}
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {d.remaining.map((s, i) => (
              <li key={i} className="flex items-center gap-2 rounded-md bg-neutral/50 p-2 text-sm">
                <span className="flex-1">{s.title}</span>
                {s.epic ? <span className="opacity-50">{s.epic}</span> : null}
                <StoryStatusBadge status={s.status} />
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

/**
 * This month's equivalent-developer-hours budget for the project owner: how many
 * hours have been spent since the start of the calendar month and how many the
 * tier still allows. Renders nothing while loading or if the tier has no
 * allowance configured.
 */
export function HoursBudget({ projectId }: { projectId: string }) {
  const q = useHoursStatus(projectId);
  if (q.isPending || q.isError || !q.data) return null;
  const s = q.data;
  if (s.allowanceHours <= 0) return null;

  const pct = Math.min(100, Math.round((s.spentThisMonth / s.allowanceHours) * 100));
  const over = s.spentThisMonth > s.allowanceHours;

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-title text-xl tracking-wide">Development hours used this month</h2>
        <span className="text-sm opacity-60">
          {fmtHours(s.spentThisMonth)} of {fmtHours(s.allowanceHours)} used
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-neutral">
        <div
          className={`h-full rounded-full ${over ? 'bg-red-600' : 'bg-primary'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="mt-1 text-xs opacity-60">Includes stories currently being built.</p>
      <p className="mt-2 text-sm opacity-70">
        {over ? (
          <span className="text-red-700">
            {fmtHours(s.spentThisMonth - s.allowanceHours)} over this month’s allowance.
          </span>
        ) : (
          <>{fmtHours(s.remaining)} remaining this month. Resets on the 1st.</>
        )}
      </p>
    </Card>
  );
}

/**
 * A compact progress bar for a project in a list (the home page and the console)
 * — a slim "where it is" indicator per project. Renders nothing until there are
 * stories to show.
 */
export function ProjectProgress({ projectId }: { projectId: string }) {
  const dash = useDashboard(projectId);
  if (dash.isPending || dash.isError || !dash.data) return null;
  const d = dash.data;
  if (d.counts.total === 0) return null;
  const pct = Math.round((d.counts.done / d.counts.total) * 100);

  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-xs opacity-70">
        <span>
          {d.counts.done} of {d.counts.total} stories built
        </span>
        {d.counts.building > 0 ? (
          <span className="text-primary">{d.counts.building} in progress</span>
        ) : (
          <span>{pct}%</span>
        )}
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-neutral">
        <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function RecentCard({
  title,
  titles,
  window,
}: {
  title: string;
  titles: string[];
  window: string;
}) {
  return (
    <Card>
      <h3 className="mb-2 font-title text-xl tracking-wide">{title}</h3>
      {titles.length === 0 ? (
        <p className="text-sm opacity-70">Nothing completed in the last {window}.</p>
      ) : (
        <ul className="list-disc space-y-0.5 pl-5 text-sm">
          {titles.map((t, i) => (
            <li key={i}>{t}</li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide opacity-60">{label}</div>
      <div className="text-2xl font-semibold">{value}</div>
    </div>
  );
}
